import { VideoStatus } from './VideoStatus';
import { Video } from './Video';
import { parseUTCDate } from '@/utils/date-utils';

export class VideoPlaylist {
  _id;
  _name;
  _userId;
  _isPinned;
  _videos;
  _publishedChild;
  _updatedAt;
  _createdAt;
  _hasUnpublishedChanges;
  _embedUrl;
  _publishedAt;
  _videoPlaylistParentId;

  static fromGraphQL(data) {
    data = {
      ...data,
      ...{
        videos: data.videos
          ? data.videos.map((v) => Video.fromGraphQL(v))
          : undefined,
        publishedChild: data.publishedChild
          ? VideoPlaylist.fromGraphQL(data.publishedChild)
          : null,
        embedUrl: data.embedUrl,
        publishedAt: data.publishedAt,
      }
    };
    return new VideoPlaylist(data);
  }

  constructor({
    id,
    name,
    userId,
    isPinned,
    videos,
    publishedChild,
    updatedAt,
    createdAt,
    hasUnpublishedChanges,
    embedUrl,
    publishedAt,
    videoPlaylistParentId,
  }) {
    this._id = id;
    this._name = name;
    this._userId = userId;
    this._isPinned = isPinned;
    this._videos = videos;
    this._publishedChild = publishedChild;
    this._updatedAt = updatedAt;
    this._createdAt = createdAt;
    this._hasUnpublishedChanges = hasUnpublishedChanges;
    this._embedUrl = embedUrl;
    this._publishedAt = publishedAt ?? null;
    this._videoPlaylistParentId = videoPlaylistParentId ?? null;
  }

  get id() {
    return this._id || undefined;
  }

  get name() {
    return this._name || undefined;
  }

  set name(name) {
    this._name = name;
  }

  get userId() {
    return this._userId || undefined;
  }

  get isPinned() {
    return this._isPinned || false;
  }

  get videos() {
    return this._videos || [];
  }

  set videos(videos) {
    this._videos = videos;
  }

  get publishedChild() {
    return this._publishedChild || null;
  }

  set publishedChild(publishedChild) {
    this._publishedChild = publishedChild;
  }

  get hasPublishedChild() {
    return !!this._publishedChild;
  }

  get createdAt() {
    return this._createdAt ? parseUTCDate(this._createdAt) : null;
  }

  get updatedAt() {
    return this._updatedAt ? parseUTCDate(this._updatedAt) : null;
  }

  get failedClips() {
    return this.videos?.filter((v) => v.hasFailures) || [];
  }

  get totalFailedClips() {
    return this.failedClips.length;
  }

  removeVideo(video) {
    this._videos = this.videos?.filter((v) => v.id !== video.id);
  }
  unpublish() {
    this._publishedChild = null;
  }

  get readyVideos() {
    return this.videos.filter((v) => v.status === VideoStatus.Ready) || [];
  }

  get thumbnails() {
    return this.publishedChild?.readyVideos.map((v) => v.thumbnail) ?? (this.readyVideos.map((v) => v.thumbnail) || []);
  }

  get totalDuration() {
    const videos = this.publishedChild?.readyVideos ?? this.readyVideos;
    const totalMilliseconds = videos.reduce((acc, v) => acc + (v.duration || 0), 0);
    const totalSeconds = Math.floor(totalMilliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  }

  get displayName() {
    if(this.name) {
      return this.name.replace(/([_\-.#@&])/g, '$1\u200B');
    }
    
    const dateObject = this.createdAt;
    const formattedDate = new Intl.DateTimeFormat('en-US', {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
    }).format(dateObject || new Date());
    const formattedTime = new Intl.DateTimeFormat('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }).format(dateObject || new Date()).replace(/\u202F/g, ' ');
    const date = `${formattedDate} ${formattedTime}`;
    return `Playlist ${date}`;
  }

  get skillIds() {
    const videos = this.publishedChild?.videos ?? this.videos;
    return [...new Set(videos.flatMap((v) => v.skills.map((s) => s.id)))];
  }

  get totalVideos() {
    return this.publishedChild?.readyVideos.length ?? this.readyVideos.length;
  }

  get hasUnpublishedChanges() {
    return this._hasUnpublishedChanges || false;
  }

  get embedUrl() {
    return this._embedUrl || undefined;
  }

  get publishedAt() {
    return this._publishedAt || null;
  }

  get videoPlaylistParentId() {
    return this._videoPlaylistParentId || null;
  }

  get formattedPublishedAt() {
    const date = this.publishedChild?._createdAt || this._createdAt;
    if (!date) return null;
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(parseUTCDate(date));
  }

  get formattedUpdatedAt() {
    return this.updatedAt ? new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(this.updatedAt) : null;
  }
}
